/**
 * Componenta pentru gestionarea contractelor Tenant-Instituție
 * Separată de contractele Medic-Instituție
 */
import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Typography
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material'
import apiClient, { institutionService } from '../../services/api'
import { TenantContract, TenantContractCreate, Institution } from '../../types/api'
import TenantSegmentPricingComponent from '../../components/Billing/TenantSegmentPricing'
import toast from 'react-hot-toast'

const TenantContractsTab: React.FC = () => {
  const [contracts, setContracts] = useState<TenantContract[]>([])
  const [totalContracts, setTotalContracts] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  
  // Filters
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState<string>('')
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedContract, setSelectedContract] = useState<TenantContract | null>(null)
  const [dialogLoading, setDialogLoading] = useState(false)
  
  // Form data
  const [formData, setFormData] = useState<TenantContractCreate>({
    institution_id: '',
    contract_number: '',
    contract_type: 'per_hour',
    hourly_rate: 0,
    currency: 'RON',
    valid_from: new Date().toISOString().split('T')[0],
    is_active: true
  })
  
  // Dropdown data
  const [institutions, setInstitutions] = useState<Institution[]>([])

  // Load contracts
  const loadContracts = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (institutionFilter) params.append('institution_id', institutionFilter)
      if (activeFilter !== '') params.append('is_active', activeFilter)
      params.append('page', String(page + 1))
      params.append('size', String(rowsPerPage))
      
      const response = await apiClient.get(`/tenant-billing/contracts?${params.toString()}`)
      setContracts(response.data.contracts)
      setTotalContracts(response.data.total)
    } catch (error: any) {
      console.error('Error loading tenant contracts:', error)
      toast.error('Eroare la încărcarea contractelor')
    } finally {
      setLoading(false)
    }
  }

  // Load institutions
  const loadInstitutions = async () => {
    try {
      const response = await institutionService.getInstitutions({ limit: 200 })
      setInstitutions(response.institutions)
    } catch (error) {
      console.error('Error loading institutions:', error)
      toast.error('Eroare la încărcarea instituțiilor')
    }
  }

  useEffect(() => {
    loadContracts()
  }, [page, rowsPerPage, institutionFilter, activeFilter])

  useEffect(() => {
    loadInstitutions()
  }, [])

  const handleCreateContract = () => {
    setSelectedContract(null)
    setFormData({
      institution_id: '',
      contract_number: '',
      contract_type: 'per_hour',
      hourly_rate: 0,
      currency: 'RON',
      valid_from: new Date().toISOString().split('T')[0],
      is_active: true
    })
    setDialogOpen(true)
  }

  const handleEditContract = (contract: TenantContract) => {
    setSelectedContract(contract)
    setFormData({
      institution_id: contract.institution_id,
      contract_number: contract.contract_number,
      contract_type: contract.contract_type,
      hourly_rate: contract.hourly_rate,
      currency: contract.currency,
      valid_from: contract.valid_from,
      valid_to: contract.valid_to,
      is_active: contract.is_active,
      notes: contract.notes
    })
    setDialogOpen(true)
  }

  const handleSaveContract = async () => {
    try {
      setDialogLoading(true)
      
      // Clean up form data based on contract type
      const cleanFormData = { ...formData }
      if (cleanFormData.contract_type === 'per_segment') {
        // Remove hourly_rate for per_segment contracts
        delete (cleanFormData as any).hourly_rate
      } else if (cleanFormData.contract_type === 'per_hour' && !cleanFormData.hourly_rate) {
        toast.error('Tariful orar este obligatoriu pentru contractele per oră')
        return
      }
      
      if (selectedContract) {
        // Update
        await apiClient.put(`/tenant-billing/contracts/${selectedContract.id}`, cleanFormData)
        toast.success('Contract actualizat cu succes')
      } else {
        // Create
        await apiClient.post('/tenant-billing/contracts', cleanFormData)
        toast.success('Contract creat cu succes')
      }
      
      setDialogOpen(false)
      loadContracts()
    } catch (error: any) {
      console.error('Error saving contract:', error)
      const errorMessage = error.response?.data?.detail || 'Eroare la salvarea contractului'
      toast.error(errorMessage)
    } finally {
      setDialogLoading(false)
    }
  }

  const handleDeleteContract = async (contractId: string) => {
    if (!confirm('Sigur doriți să ștergeți acest contract?')) {
      return
    }

    try {
      await apiClient.delete(`/tenant-billing/contracts/${contractId}`)
      toast.success('Contract șters cu succes')
      loadContracts()
    } catch (error: any) {
      console.error('Error deleting contract:', error)
      const errorMessage = error.response?.data?.detail || 'Eroare la ștergerea contractului'
      toast.error(errorMessage)
    }
  }

  const handleDownloadPdf = async (contract: TenantContract) => {
    try {
      toast.loading('Se generează PDF-ul...', { id: 'pdf-loading' })
      
      const response = await apiClient.get(`/tenant-billing/contracts/${contract.id}/pdf`, {
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const filename = `contract_${contract.institution_name?.replace(/\s+/g, '_') || 'export'}_${new Date().toISOString().split('T')[0]}.pdf`
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      toast.success('PDF descărcat cu succes', { id: 'pdf-loading' })
    } catch (error: any) {
      console.error('Error downloading PDF:', error)
      toast.error(error.response?.data?.detail || 'Eroare la generarea PDF-ului', { id: 'pdf-loading' })
    }
  }

  return (
    <Box>
      {/* Filters - compact */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Instituție</InputLabel>
              <Select
                value={institutionFilter}
                onChange={(e) => setInstitutionFilter(e.target.value)}
                label="Instituție"
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
                {institutions.map((inst) => (
                  <MenuItem key={inst.id} value={inst.id} sx={{ fontSize: '0.8rem' }}>
                    {inst.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
                <MenuItem value="true" sx={{ fontSize: '0.8rem' }}>Active</MenuItem>
                <MenuItem value="false" sx={{ fontSize: '0.8rem' }}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={loadContracts}
              disabled={loading}
              sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
            >
              Reîncarcă
            </Button>
          </Grid>

          <Grid item xs={12} sm={4} md={2}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleCreateContract}
              sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
            >
              Contract Nou
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Contracts Table - compact with dark header */}
      <TableContainer component={Paper} sx={{
        '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 },
        '& .MuiChip-root': { height: 20, fontSize: '0.75rem' },
        '& .MuiTableCell-root': { py: 0.5, px: 1 },
        '& .MuiTableBody-root .MuiIconButton-root': { p: '3px' },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{
              bgcolor: 'grey.900',
              '& th': {
                color: 'common.white',
                fontSize: '0.8rem',
                fontWeight: 700,
                py: 1,
                px: 1.5,
                whiteSpace: 'nowrap'
              }
            }}>
              <TableCell>Nr. Contract</TableCell>
              <TableCell>Instituție</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Tarif/Oră</TableCell>
              <TableCell>Perioada</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Se încarcă...</TableCell>
              </TableRow>
            ) : contracts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nu există contracte
                </TableCell>
              </TableRow>
            ) : (
              contracts.map((contract) => (
                <TableRow key={contract.id}>
                  <TableCell>
                    <strong>{contract.contract_number}</strong>
                  </TableCell>
                  <TableCell>{contract.institution_name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={contract.contract_type === 'per_hour' ? 'Per Oră' : 'Per Segment'} 
                      size="small" 
                      color="info"
                    />
                  </TableCell>
                  <TableCell>
                    {contract.hourly_rate} {contract.currency}
                  </TableCell>
                  <TableCell>
                    {new Date(contract.valid_from).toLocaleDateString('ro-RO')}
                    {contract.valid_to && ` - ${new Date(contract.valid_to).toLocaleDateString('ro-RO')}`}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={contract.is_active ? 'Activ' : 'Inactiv'} 
                      color={contract.is_active ? 'success' : 'default'} 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditContract(contract)} title="Editează">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {contract.contract_type === 'per_segment' && (
                      <IconButton 
                        size="small" 
                        color="secondary" 
                        onClick={() => handleDownloadPdf(contract)}
                        title="Descarcă PDF"
                      >
                        <PdfIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => handleDeleteContract(contract.id)} title="Șterge">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalContracts}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          labelRowsPerPage="Rânduri per pagină:"
        />
      </TableContainer>

      {/* Create/Edit Dialog - identical to QuickAddPatientForm */}
      <Dialog 
        open={dialogOpen} 
        onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') setDialogOpen(false) }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              fontSize: '0.85rem',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'primary.main',
            },
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiSelect-select': {
              color: 'text.primary',
            },
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
            },
          }
        }}
      >
        <DialogTitle sx={{ 
          py: 1.5, 
          px: 2,
          fontSize: '1rem',
          fontWeight: 600,
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary'
        }}>
          {selectedContract ? 'Editare Contract Instituție' : 'Contract Nou Instituție'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Instituție</InputLabel>
                <Select
                  value={formData.institution_id}
                  onChange={(e) => setFormData({ ...formData, institution_id: e.target.value })}
                  label="Instituție"
                  disabled={!!selectedContract}
                >
                  {institutions.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Număr Contract"
                value={formData.contract_number}
                onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                helperText="1-13 caractere numerice"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Tip Contract</InputLabel>
                <Select
                  value={formData.contract_type}
                  onChange={(e) => setFormData({ ...formData, contract_type: e.target.value as 'per_hour' | 'per_segment' })}
                  label="Tip Contract"
                >
                  <MenuItem value="per_hour">Per Oră</MenuItem>
                  <MenuItem value="per_segment">Per Segment</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.contract_type === 'per_hour' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Tarif per Oră (RON)"
                  type="number"
                  value={formData.hourly_rate || ''}
                  onChange={(e) => {
                    const value = e.target.value
                    setFormData({ 
                      ...formData, 
                      hourly_rate: value === '' ? 0 : parseFloat(value) 
                    })
                  }}
                />
              </Grid>
            )}

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Data Început"
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Data Sfârșit"
                type="date"
                value={formData.valid_to || ''}
                onChange={(e) => setFormData({ ...formData, valid_to: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Note"
                multiline
                rows={2}
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>

          {/* Segment Pricing Section - only for per_segment contracts */}
          {formData.contract_type === 'per_segment' && (
            <>
              {selectedContract ? (
                <TenantSegmentPricingComponent
                  contractId={selectedContract.id}
                  currency={formData.currency || 'RON'}
                />
              ) : (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: 1.5, 
                    py: 0.5,
                    bgcolor: 'info.dark',
                    color: 'info.contrastText',
                    '& .MuiAlert-icon': { color: 'info.contrastText' }
                  }}
                >
                  <Typography sx={{ fontSize: '0.8rem', color: 'inherit' }}>
                    <strong>Prețuri per Segment:</strong> După creare, veți putea configura prețurile per body part.
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between'
        }}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                size="small"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label={<Typography sx={{ fontSize: '0.78rem' }}>Activ</Typography>}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={() => setDialogOpen(false)}
              disabled={dialogLoading}
              size="small"
              sx={{ fontSize: '0.8rem' }}
            >
              Anulează
            </Button>
            <Button
              onClick={handleSaveContract}
              variant="contained"
              size="small"
              disabled={
                dialogLoading || 
                !formData.institution_id || 
                !formData.contract_number || 
                (formData.contract_type === 'per_hour' && !formData.hourly_rate)
              }
              sx={{ fontSize: '0.8rem' }}
            >
              {selectedContract ? 'Actualizează' : 'Creează'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TenantContractsTab
